"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import PWAInstallButton from "@/shared/components/common/pwa-install-button";
import CtaLink from "@/shared/components/common/CtaLink";

export default function LandingHeroNav() {
  return (
    <div className="relative">
      {/* Hero section — white bg, full viewport height */}
      <section className="relative w-full bg-white overflow-hidden min-h-[100svh] flex flex-col">
        {/* Nav */}
        <nav className="relative z-20 flex items-center justify-between px-5 py-3 sm:px-10 sm:py-4">
          <Link href="/">
            <img src="/logo.png" alt="Hey Hoku - AI personal stylist" width={256} height={263} className="h-9 sm:h-11 w-auto" />
          </Link>
          <div className="hidden sm:flex items-center gap-6">
            <Link href="/pricing" className="font-winky text-[14px] text-[#6b6b6b] hover:text-[#2d2d2d] transition-colors">Pricing</Link>
            <Link href="/about" className="font-winky text-[14px] text-[#6b6b6b] hover:text-[#2d2d2d] transition-colors">About</Link>
            <Link href="/blog" className="font-winky text-[14px] text-[#6b6b6b] hover:text-[#2d2d2d] transition-colors">Blog</Link>
          </div>
          <div className="flex items-center gap-2">
            <PWAInstallButton />
          </div>
        </nav>

        {/* Hero content — stacked on mobile, side-by-side on desktop */}
        <div className="max-w-6xl mx-auto px-6 flex-1 flex flex-col lg:flex-row lg:items-center lg:gap-12 justify-center">
          {/* Text side */}
          <div className="text-center lg:text-left lg:flex-1">
            <motion.h1
              className="font-winky font-bold text-[#2d2d2d] text-[2.25rem] sm:text-[2.75rem] lg:text-[3.5rem] lg:whitespace-nowrap leading-[1.14] tracking-tight mb-4 sm:mb-5"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Stop guessing.{" "}<br />You already have a style{" "}<br />shop with it.
            </motion.h1>

            <motion.p
              className="text-[#6b6b6b] text-[14px] sm:text-[16px] leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-8 font-patrick"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              Hey Hoku is your AI wardrobe intelligence that helps you decide
              what to wear and what to buy based on your wardrobe, body
              profile, colour compatibility, preferences, and occasions.
              <span className="block mt-3 font-winky font-semibold text-[#2d2d2d]">
                Because the best outfit is not the one that looks good online.
                It&apos;s the one that works on you.
              </span>
            </motion.p>

            <motion.div
              className="flex flex-row items-center justify-center lg:justify-start gap-3"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <CtaLink
                ctaName="get_started"
                ctaLocation="hero"
                href="/login?action=signup"
                className="group inline-flex items-center gap-2 rounded-full bg-[#ffd372] text-[#2d2d2d] border border-[#4c4c4c] font-winky font-bold text-[14px] sm:text-[15px] px-8 py-3.5 transition-all duration-300 hover:scale-105 active:scale-95 shadow-[2px_2px_0_#4c4c4c]"
              >
                <span>Get Started</span>
                <span className="transition-transform duration-300 group-hover:translate-x-0.5">
                  &rarr;
                </span>
              </CtaLink>
              <CtaLink
                ctaName="try_demo"
                ctaLocation="hero"
                href="/demo"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#2d2d2d] border border-[#4c4c4c] font-winky font-bold text-[14px] sm:text-[15px] px-8 py-3.5 transition-all duration-300 hover:scale-105 active:scale-95 shadow-[2px_2px_0_#4c4c4c]"
              >
                Explore
              </CtaLink>
            </motion.div>
          </div>

          {/* Image side */}
          <motion.div
            className="relative max-w-xs mx-auto lg:mx-0 lg:max-w-sm lg:flex-1 mt-8 lg:mt-0"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <img
              src="/landing-page/hoku-mascot.jpeg"
              alt="Hoku AI Style Assistant"
              width={1131}
              height={1280}
              className="w-full h-auto object-contain"
              fetchPriority="high"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
}
